import { Link, useNavigate } from 'react-router-dom';
import { getSizeOptions } from '@/lib/argentoSizes';
import { whatsappProductLink } from '@/lib/whatsapp';
import { ShoppingBag, Heart, MessageCircle } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { toast } from 'sonner';

/**
 * ProductCard — editorial. Token-driven, no hardcoded hex.
 * Radius: 4px (md) on image and badges. Hairline border via --line.
 */
export default function ProductCard({ product, className = '' }) {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const saved = isInWishlist(product.id);

  const formatPrice = (price) =>
    new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(price);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.price_on_request) {
      window.open(whatsappProductLink(product), '_blank', 'noopener');
      return;
    }
    if (getSizeOptions(product).length) {
      toast('Scegli la misura', { description: product.name });
      navigate(`/prodotto/${product.id}`);
      return;
    }
    const success = await addToCart(product.id);
    if (success) {
      toast.success('Prodotto aggiunto al carrello', { description: product.name });
    } else {
      toast.error("Errore nell'aggiungere al carrello");
    }
  };

  const handleToggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleWishlist(product.id);
    toast.success(added ? 'Aggiunto ai preferiti' : 'Rimosso dai preferiti', {
      description: product.name,
    });
  };

  return (
    <Link
      to={`/prodotto/${product.id}`}
      className={`group block ${className}`}
      data-testid={`product-card-${product.id}`}
      aria-label={`${product.name} — ${product.price_on_request ? 'Prezzo su richiesta' : formatPrice(product.price)}`}
    >
      <article className="relative transition-colors duration-enter">
        {/* Image */}
        <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-secondary mb-5 image-shine">
          <img
            src={product.image_url}
            alt={`${product.name} — ${product.material} — ${product.category}`}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/35 transition-colors duration-enter ease-out flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex gap-3 translate-y-3 group-hover:translate-y-0 transition-transform duration-enter ease-out">
              <button
                onClick={handleAddToCart}
                className="w-12 h-12 bg-ivory rounded-full flex items-center justify-center text-ink hover:bg-bordeaux hover:text-ivory transition-colors duration-hover"
                data-testid={`add-to-cart-${product.id}`}
                aria-label={product.price_on_request ? `Chiedi il prezzo di ${product.name} su WhatsApp` : `Aggiungi ${product.name} al carrello`}
              >
                {product.price_on_request ? <MessageCircle className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
              </button>
              <button
                onClick={handleToggleWishlist}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-hover ${
                  saved ? 'bg-bordeaux text-ivory' : 'bg-ivory text-ink hover:bg-bordeaux hover:text-ivory'
                }`}
                data-testid={`wishlist-toggle-${product.id}`}
                aria-label={saved ? `Rimuovi ${product.name} dai preferiti` : `Aggiungi ${product.name} ai preferiti`}
                aria-pressed={saved}
              >
                <Heart className="w-4 h-4" fill={saved ? 'currentColor' : 'none'} />
              </button>
            </div>
          </div>

          {/* Persistent wishlist heart (top-right) */}
          <button
            onClick={handleToggleWishlist}
            className={`absolute top-3 right-3 w-9 h-9 rounded-full backdrop-blur flex items-center justify-center transition-all duration-hover z-10 ${
              saved
                ? 'bg-bordeaux text-ivory opacity-100'
                : 'bg-ivory/90 text-ink opacity-0 group-hover:opacity-100 hover:bg-bordeaux hover:text-ivory'
            }`}
            data-testid={`wishlist-badge-${product.id}`}
            aria-label={saved ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
            aria-pressed={saved}
          >
            <Heart className="w-4 h-4" fill={saved ? 'currentColor' : 'none'} />
          </button>

          {/* Featured badge */}
          {product.featured && (
            <span className="absolute top-3 left-3 bg-bordeaux text-ivory text-eyebrow px-3 py-1.5 rounded-full">
              In Evidenza
            </span>
          )}

          {/* Category badge */}
          <span className="absolute bottom-3 right-3 bg-ivory/95 text-ink text-eyebrow px-3 py-1.5 rounded-full">
            {product.category}
          </span>
        </div>

        {/* Info */}
        <div className="space-y-1.5">
          <p className="text-eyebrow text-bordeaux">
            {product.brand ? product.brand : product.material}
          </p>
          <h3 className="font-display text-h4 text-ink group-hover:text-bordeaux transition-colors duration-hover">
            {product.name}
          </h3>
          <p className="text-ink text-base font-medium" data-testid={`product-card-price-${product.id}`}>
            {product.price_on_request ? <span className="italic text-ink/70">Prezzo su richiesta</span> : formatPrice(product.price)}
          </p>
        </div>
      </article>
    </Link>
  );
}
